import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { loadInitialNotifications } from '../../store/redux/actions/notificationActions';
import { revalidateUserConfig } from '../../store/redux/actions/userActionCreator';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Footer from '../footer';
import { useFilteredNavigation } from '../../hooks/useFilteredNavigation';
import { useCompanyRulesSignalR } from '../../hooks/useCompanyRulesSignalR';
import PendingTermsGate from '../terms/PendingTermsGate';
import SubscriptionBanner from '../subscription/SubscriptionBanner';
import LockedShell from '../subscription/LockedShell';
import { useMySubscription } from '../../hooks/useMySubscription';
import { getLockedModule } from '../../data/lockedRoutes';
import { Skeleton } from '../ui/Skeleton';

/**
 * Master layout component combining Sidebar, TopBar, Footer with content outlet.
 *
 * Layout Structure (viewport-height based):
 * - Sidebar on the left spanning full height (header aligns with TopBar)
 * - Right section with:
 *   - TopBar at top
 *   - Main content with independent scrolling
 *   - Footer at bottom
 *
 * Provides:
 * - Responsive sidebar (desktop: collapsible, mobile: overlay)
 * - Top navigation bar with search, notifications, user menu
 * - Content area with React Router Outlet
 * - Role-based navigation filtering via useFilteredNavigation hook
 * - No horizontal scroll on any standard viewport
 */
const MasterLayout: React.FC = () => {
  const { t } = useTranslation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Get role-filtered navigation modules
  const filteredModules = useFilteredNavigation();

  // Subscribe to real-time company rules updates via SignalR
  useCompanyRulesSignalR();

  const dispatch = useDispatch();
  const userId = useSelector((state: any) => state.user.userId);

  // Load initial notification count for bell badge
  useEffect(() => {
    if (userId) {
      (dispatch as any)(loadInitialNotifications(userId));
    }
  }, [userId, dispatch]);

  // Reconcile the persisted Redux snapshot with the server once per app load.
  // The whole user slice is cached in localStorage (redux-persist) and the
  // firstLoad-gated refresh in PrivateRoute never re-runs for a returning
  // session, so config like the attendance-image / face-recognition rules
  // (which decide whether the clocking dialog opens the camera) can be stale
  // after an admin changes it while this session was closed. This re-fetches
  // getUserInfo once and updates Redux only for the fields that changed.
  const didRevalidate = useRef(false);
  useEffect(() => {
    if (didRevalidate.current || !userId) return;
    didRevalidate.current = true;
    (dispatch as any)(revalidateUserConfig());
  }, [userId, dispatch]);

  // ---------- Phase 5: route-level Free-tier lock ----------
  // Resolves the locked-module slug for the current pathname when the caller
  // is Free-tier AND not an Operator. We also honour the Operator preview
  // hook `?previewTier=free` so operators can dogfood the soft-lock without
  // mutating company state — but only when the live session is actually
  // an Operator (mirrors SubscriptionBanner's preview guard so a regular
  // user can't smuggle in the lock via URL).
  const location = useLocation();
  const { isFree, isOperator, data: subscriptionData, isError: subscriptionError } =
    useMySubscription();
  // The /api/Subscription/me payload is what tells us the tier. Before it
  // resolves, useMySubscription falls back to "Free" (coerceTier(undefined)
  // returns "Free"), which would briefly wrap Paid/Trial users in LockedShell
  // and flash the locked-preview chrome at them on every page load. Gate the
  // soft-lock decision on the first response (success OR error). On error we
  // fail open — better to give the user their full app than block them
  // indefinitely if /me momentarily 5xxs.
  const subscriptionReady = subscriptionData !== null || subscriptionError;
  const previewFree = React.useMemo(() => {
    if (!isOperator) return false;
    try {
      const params = new URLSearchParams(location.search);
      return (params.get('previewTier') || '').toLowerCase() === 'free';
    } catch {
      return false;
    }
  }, [isOperator, location.search]);
  const treatAsFree = subscriptionReady && ((isFree && !isOperator) || previewFree);
  const lockedModule = treatAsFree ? getLockedModule(location.pathname) : null;

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Pending platform-terms acceptance — mounts a non-dismissible modal when
          the user owes acceptances on the currently published versions. */}
      <PendingTermsGate />

      {/* Desktop Sidebar - full height, next to topbar */}
      <Sidebar
        modules={filteredModules}
        isCollapsed={isSidebarCollapsed}
        onToggle={toggleSidebar}
      />

      {/* Mobile Sidebar Overlay (only when open) */}
      {isMobileSidebarOpen && (
        <Sidebar
          modules={filteredModules}
          isCollapsed={false}
          onToggle={toggleMobileSidebar}
        />
      )}

      {/* Right Section: TopBar + Content + Footer */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top Navigation Bar */}
        <TopBar onMobileMenuClick={toggleMobileSidebar} />

        {/* Main Content Area - independent scroll.
            The SubscriptionBanner is mounted as the FIRST child inside <main>
            so it sticks to the top of the scrollable area (i.e. directly under
            the TopBar) without needing to invent a third flex row. The banner
            itself is `position: sticky; top: 0` and removes its own padding so
            the band can stretch edge-to-edge.
            The PendingTermsGate's modal is z-[100]+ — our banner sits at z-30
            and the brochure overlay at z-40, well clear of it. */}
        <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-background">
          <SubscriptionBanner />
          <div className="p-4 md:p-6">
            {!subscriptionReady ? (
              <PageContentSkeleton />
            ) : lockedModule ? (
              <LockedShell moduleSlug={lockedModule}>
                <Outlet />
              </LockedShell>
            ) : (
              <Outlet />
            )}
          </div>
        </main>

        {/* Footer */}
        <Footer>
          <span style={{ fontSize: '10px' }}>
            {t("Copyrights Powered by Mowazaf Technologies All Rights Reserved", { year: new Date().getFullYear() })}
          </span>
        </Footer>
      </div>
    </div>
  );
};

/**
 * Generic page placeholder shown while /api/Subscription/me is resolving.
 * Mirrors the typical PageShell rhythm — header strip, then a few content
 * cards — so the layout shift when the real page mounts is minimal.
 */
const PageContentSkeleton: React.FC = () => (
  <div className="space-y-6" aria-label="Loading">
    {/* Page header row: title + actions */}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-32" />
      </div>
    </div>
    {/* Stat cards row */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
    {/* Main content block */}
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <Skeleton className="h-5 w-40" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-10/12" />
        <Skeleton className="h-4 w-9/12" />
      </div>
    </div>
  </div>
);

export default MasterLayout;
